import React, { useEffect } from 'react';
import { useQuery } from 'react-query';

import { CheckCircleFilled } from '@ant-design/icons';
import { CheckboxChangeEvent } from 'antd/lib/checkbox';
import styled from 'styled-components';

import { getAllCommunities } from 'apis/CommunityAPI';
import Checkbox from 'components/lib/Checkbox';
import { COMPANY_COMMUNITY_RELATION_TYPE } from 'constants/company';
import { PAGINATION_PAGE_SIZE } from 'constants/pagination';
import { useDebounce } from 'utils/hooks/useDebounce';

import ShopRequestModalCheckboxes from './ShopRequestModalCheckboxes';
import { useShopRequestModalContext } from './ShopRequestModalContext';
import ShopRequestModalDropdown from './ShopRequestModalDropdown';
import ShopRequestsModalPropagation from './ShopRequestsModalPropagation';
import { SHOP_REQUEST_TYPE, SHOP_REQUEST_TYPE_LABELS } from '../constants';
import { getBaseCommunityShopRequest } from '../utils';

const Container = styled.div`
  margin: 0 30px 30px;
  display: flex;
  flex-flow: column;
`;

const Title = styled.div`
  font-size: 12px;
  font-family: var(--font-bold);
  letter-spacing: 1px;
  width: 480px;
  display: flex;
  align-items: center;
`;

const StyledCheckbox = styled(Checkbox)`
  margin-left: 30px;
  font-size: 14px;
  font-family: var(--font-bold);
  &&& .ant-checkbox + span {
    padding-inline-start: 10px;
    padding-inline-end: 30px;
    display: flex;
    align-items: center;
  }
`;

const Row = styled.div`
  display: flex;
  flex-flow: row;
  align-items: center;
  margin-bottom: 10px;
`;

const BaseCommunityNotice = styled.div`
  align-items: center;
  background: var(--positive-green-background);
  border: 1px solid var(--positive-green-border-light);
  color: var(--positive-green-dark);
  display: flex;
  font-size: 12px;
  gap: 8px;
  margin-bottom: 20px;
  padding: 10px;
`;

const NoticeIcon = styled(CheckCircleFilled)`
  color: var(--positive-green);
`;

const ShopRequestModalBaseCommunitySelect = () => {
  const {
    selectedCompany,
    shopForBaseCommunity,
    selectedBaseCommunity,
    baseCommunityIL,
    baseCommunityAL,
    baseCommunityMC,
    repeat,
    shopperId,
    auditorId,
    dueDate,
    specificDueDate,
    requestFrom,
    requestType,
    baseCommunityPropagateSelf,
    baseCommunityPropagateOthers,
    setBaseCommunityIL,
    setBaseCommunityAL,
    setBaseCommunityMC,
    setShopForBaseCommunity,
    setSelectedBaseCommunity,
    setBaseCommunityPropagateOthers,
    setBaseCommunityPropagateSelf,
    setShopRequests,
  } = useShopRequestModalContext();

  const [search, setSearch] = React.useState<string>('');
  const debouncedSearch = useDebounce(search);

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['company', 'communities', selectedCompany, debouncedSearch],
    queryFn: () =>
      getAllCommunities({
        companies: [selectedCompany],
        search: debouncedSearch,
        companies_relation: COMPANY_COMMUNITY_RELATION_TYPE.COMMUNITY,
        page_size: PAGINATION_PAGE_SIZE.MODERATE,
      }),
    select: (data: any) => data?.results,
    enabled: !!selectedCompany,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    setSearch('');
    setSelectedBaseCommunity(null);
  }, [selectedCompany]);

  const isPricingRequest = requestType === SHOP_REQUEST_TYPE.MYSTERY_SHOP;

  useEffect(() => {
    setSearch('');
    setShopRequests([]);
  }, [selectedBaseCommunity]);

  useEffect(() => {
    if (!isPricingRequest) {
      setBaseCommunityIL(false);
      setBaseCommunityAL(false);
      setBaseCommunityMC(false);
      setBaseCommunityPropagateSelf(false);
      setBaseCommunityPropagateOthers(false);
    }
  }, [
    isPricingRequest,
    setBaseCommunityAL,
    setBaseCommunityIL,
    setBaseCommunityMC,
    setBaseCommunityPropagateOthers,
    setBaseCommunityPropagateSelf,
  ]);

  const disableCheckboxes = !selectedBaseCommunity || !shopForBaseCommunity;
  const shopRequest = getBaseCommunityShopRequest({
    baseCommunityId: selectedBaseCommunity?.id,
    independentLiving: baseCommunityIL,
    assistedLiving: baseCommunityAL,
    memoryCare: baseCommunityMC,
    dueDate,
    specificDueDate,
    repeat,
    shopperId,
    auditorId,
    requestFrom,
    requestType,
    selectedCompany,
    propagation: {
      propagate_self: baseCommunityPropagateSelf,
      propagate_others: baseCommunityPropagateOthers,
    },
  });

  return (
    <Container>
      <Row>
        <Title>BASE COMMUNITY</Title>
        {isPricingRequest && (
          <StyledCheckbox
            checked={shopForBaseCommunity}
            onChange={(event: CheckboxChangeEvent) => {
              setShopForBaseCommunity(event.target.checked);
              setBaseCommunityIL(event.target.checked);
              setBaseCommunityAL(event.target.checked);
              setBaseCommunityMC(event.target.checked);
              setBaseCommunityPropagateOthers(event.target.checked);
              if (baseCommunityPropagateSelf) {
                setBaseCommunityPropagateSelf(event.target.checked);
              }
            }}
            disabled={!selectedCompany || !selectedBaseCommunity}
            className={'green'}
          >
            Shop for base community as well
          </StyledCheckbox>
        )}
      </Row>
      <Row>
        <ShopRequestModalDropdown
          communities={data}
          value={selectedBaseCommunity?.id}
          loading={isLoading || isFetching}
          disabled={!selectedCompany}
          placeholder="Search for community"
          onChange={(value: any) => setSelectedBaseCommunity(value)}
          onSearch={setSearch}
          width={isPricingRequest ? '480px' : '100%'}
        />
        {isPricingRequest && (
          <ShopRequestModalCheckboxes
            independentLiving={baseCommunityIL && !disableCheckboxes}
            assistedLiving={baseCommunityAL && !disableCheckboxes}
            memoryCare={baseCommunityMC && !disableCheckboxes}
            disabled={disableCheckboxes}
            toggleIndependentLiving={setBaseCommunityIL}
            toggleAssistedLiving={setBaseCommunityAL}
            toggleMemoryCare={setBaseCommunityMC}
          />
        )}
      </Row>
      {!isPricingRequest && selectedBaseCommunity && (
        <BaseCommunityNotice>
          <NoticeIcon />A new shop request will also be created automatically for the selected base community as part of
          this {SHOP_REQUEST_TYPE_LABELS[requestType]} request.
        </BaseCommunityNotice>
      )}
      {isPricingRequest && (
        <ShopRequestsModalPropagation
          shopRequest={shopRequest}
          community={selectedBaseCommunity}
          isBaseCommunity
          disabled={!shopForBaseCommunity}
        />
      )}
    </Container>
  );
};

export default ShopRequestModalBaseCommunitySelect;
